import * as React from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export type ToastVariant = 'default' | 'success' | 'destructive' | 'warning'

export interface ToastItem {
  id: string
  title?: string
  description?: string
  variant?: ToastVariant
}

type Listener = () => void

interface ToastStore {
  toasts: ToastItem[]
  listeners: Set<Listener>
  generation: number
}

// The store lives on globalThis (not in a plain module variable) so the
// imperative toast() API and the mounted <Toaster/> always share the same
// state across Vite HMR reloads of this file.
const store: ToastStore = ((
  globalThis as typeof globalThis & { __tigerflowToastStore?: ToastStore }
).__tigerflowToastStore ??= {
  toasts: [],
  listeners: new Set(),
  generation: 0,
})

// Each time this module is evaluated (initial load or HMR), invalidate any
// Toaster instances still subscribed from a previous module instance. Those
// ghosts would otherwise keep rendering the same store.toasts and look like
// duplicated notifications.
const prevListeners = [...store.listeners]
store.generation += 1
store.listeners.clear()
store.toasts = []
const generation = store.generation
prevListeners.forEach(l => {
  try {
    l()
  } catch {
    // Zombie setState from an unmounted fiber — ignore.
  }
})

function emit() {
  store.listeners.forEach(l => l())
}

export function toast(input: Omit<ToastItem, 'id'>) {
  const variant = input.variant ?? 'default'
  // Skip stacking an identical toast that's already visible so spam clicks
  // (e.g. hammering "Next") don't flood the screen with the same message.
  const existing = store.toasts.find(
    tItem =>
      tItem.title === input.title &&
      tItem.description === input.description &&
      (tItem.variant ?? 'default') === variant
  )
  if (existing) return existing.id

  const id = Math.random().toString(36).slice(2)
  store.toasts = [...store.toasts, { ...input, id, variant }]
  emit()
  return id
}

export function dismissToast(id: string) {
  store.toasts = store.toasts.filter(t => t.id !== id)
  emit()
}

function subscribe(onStoreChange: Listener) {
  store.listeners.add(onStoreChange)
  return () => {
    store.listeners.delete(onStoreChange)
  }
}

function getSnapshot() {
  return store.toasts
}

function useToastStore() {
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

const variantClasses: Record<ToastVariant, string> = {
  default: 'border-gray-200 bg-white text-gray-900',
  success: 'border-green-600 bg-green-600 text-white',
  destructive: 'border-red-600 bg-red-600 text-white',
  warning: 'border-amber-500 bg-amber-500 text-white',
}

export function Toaster() {
  const { t: translate } = useTranslation('shell')
  const items = useToastStore()

  // HMR can leave a previous Toaster fiber mounted. Only the Toaster from the
  // latest module generation may render — stale ones return null so their
  // leftover toast DOM unmounts instead of stacking duplicates.
  if (generation !== store.generation) return null

  return (
    <ToastPrimitive.Provider swipeDirection='right' duration={3000}>
      {items.map(t => (
        <ToastPrimitive.Root
          key={t.id}
          open
          onOpenChange={open => {
            if (!open) dismissToast(t.id)
          }}
          className={cn(
            'pointer-events-auto relative flex w-full items-start justify-between gap-3 rounded-md border p-4 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out',
            variantClasses[t.variant ?? 'default']
          )}
        >
          <div className='grid gap-1'>
            {t.title && (
              <ToastPrimitive.Title className='text-sm font-semibold'>
                {t.title}
              </ToastPrimitive.Title>
            )}
            {t.description && (
              <ToastPrimitive.Description className='text-sm opacity-90'>
                {t.description}
              </ToastPrimitive.Description>
            )}
          </div>
          <ToastPrimitive.Close
            className='opacity-70 hover:opacity-100'
            aria-label={translate('toast.close')}
          >
            <X className='h-4 w-4' />
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className='fixed bottom-0 right-0 z-9999 flex max-h-screen w-full flex-col gap-2 p-4 sm:max-w-sm' />
    </ToastPrimitive.Provider>
  )
}
